import { Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { Button } from '@worldcoo/ui/components/button';
import { CircleArrowDownIcon } from 'lucide-react';
import { Statistics } from './statistics';
import { useTranslation } from '@/lib/i18n';
import { Image } from '../../-components/image';
import { usePortalMetadataQuery } from '@/lib/queries';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { sanitizeHTML } from '@worldcoo/ui/lib/sanitize-html';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { NotTranslatedInfo } from './not-translated-info';
import { isNotFound } from '@tanstack/react-router';

export function Hero() {
	return (
		<Suspense
			fallback={
				<div className="flex flex-col items-center px-6 py-20 lg:flex-row">
					<section className="flex w-full flex-col gap-6 px-6 py-10 lg:w-1/2">
						<Skeleton className="bg-secondary h-9 w-32" />

						<div className="flex flex-col gap-1">
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-sm" />
						</div>

						<Skeleton className="bg-secondary h-14 w-48" />
					</section>

					<div className="w-full max-w-[720px] px-6 py-20 sm:p-10 lg:w-1/2">
						<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
					</div>
				</div>
			}
		>
			<SuspensedHero />
		</Suspense>
	);
}

function SuspensedHero() {
	const { i18n } = useTranslation();
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<DivWithPrimaryColorVariable className="flex flex-col items-center gap-12 p-6 lg:flex-row lg:gap-20 lg:py-20">
			<section className="flex w-full flex-1 flex-col lg:w-1/2">
				{metadata.textsNotFound && <NotTranslatedInfo className="mb-4" />}

				<h1 className="text-[32px] font-medium lg:text-[40px]">{metadata.texts.title}</h1>

				{metadata.texts.presentation && (
					<div
						className="unreset-tailwind"
						dangerouslySetInnerHTML={{
							__html: sanitizeHTML(metadata.texts.presentation),
						}}
					/>
				)}

				<Button
					size="2xl"
					className="cursor-pointer sm:w-fit"
					onClick={() => document.getElementById('campaigns')?.scrollIntoView({ behavior: 'smooth' })}
					style={{ backgroundColor: 'var(--primary)' }}
				>
					{metadata.texts.donateButton ?? i18n.portal.heroButton}
					<CircleArrowDownIcon strokeWidth={1.25} />
				</Button>
			</section>

			<div className="relative w-full max-w-fit lg:w-1/2">
				<Image
					src={metadata.media?.location}
					height={heroImageSize.height}
					aspectRatio={heroImageSize.width / heroImageSize.height}
					className={cn('bg-background w-full rounded-2xl object-cover', heroImageSize.className)}
					alt={metadata.imageAlt ?? 'hero-image'}
					fetchPriority="high"
				/>

				<div
					className={cn(
						'bg-primary rotate-5 absolute bottom-0 left-0 right-0 top-0 -z-10 w-full rounded-lg',
						heroImageSize.className,
					)}
				/>

				<div className="relative -mt-20 w-full p-1.5 lg:absolute lg:bottom-0 lg:mt-0 lg:p-5">
					<Statistics />
				</div>
			</div>
		</DivWithPrimaryColorVariable>
	);
}
